export type Platform = 'ios' | 'android' | 'desktop';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Captured at module load, not in a component. The browser fires this once,
// early, and a listener registered later simply never hears it.
let captured: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

const notify = (): void => {
    listeners.forEach((listener) => listener());
};

window.addEventListener('beforeinstallprompt', (event) => {
    // Suppressing the browser's own mini-infobar so the app can offer install
    // at a moment that makes sense -- right after signing up.
    event.preventDefault();
    captured = event as BeforeInstallPromptEvent;
    notify();
});

window.addEventListener('appinstalled', () => {
    captured = null;
    notify();
});

export const subscribeToInstallPrompt = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const hasInstallPrompt = (): boolean => captured !== null;

/** Returns true when the browser accepted the install. */
export const showInstallPrompt = async (): Promise<boolean> => {
    if (!captured) return false;
    await captured.prompt();
    const { outcome } = await captured.userChoice;
    // The event is single-use; the browser will fire a fresh one if it wants to.
    captured = null;
    notify();
    return outcome === 'accepted';
};

export const isStandalone = (): boolean =>
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari's own flag, which predates the standard media query.
    ('standalone' in navigator && navigator.standalone === true);

export const detectPlatform = (): Platform => {
    const ua = navigator.userAgent;
    if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
    // iPadOS reports itself as a Mac; touch points are what separate them.
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return 'ios';
    if (/android/i.test(ua)) return 'android';
    return 'desktop';
};
